import React, { useEffect, useMemo, useState } from "react";
import { DollarSign, Edit3, RotateCcw, X, Check, Search } from "lucide-react";
import api, { formatErr } from "../lib/api";

/** RateCardEditor — project-scoped overrides for the global RATE_DATABASE.
 *
 *  Backend endpoints used:
 *    GET    /api/projects/{id}/rate-card           → merged catalog
 *    PUT    /api/projects/{id}/rate-card/{code}    → upsert lo/hi/desc/...
 *    DELETE /api/projects/{id}/rate-card/{code}    → revert to default
 */
export default function RateCardEditor({ projectId }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  const [domain, setDomain] = useState("All");
  const [editing, setEditing] = useState(null); // {code, lo, hi, desc}

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get(`/projects/${projectId}/rate-card`);
      setItems(data.items || []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    } finally { setBusy(false); }
  };

  useEffect(() => { if (projectId) load(); }, [projectId]); // eslint-disable-line

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return items.filter((it) => {
      if (domain !== "All" && (it.cat || "") !== domain) return false;
      if (!q) return true;
      return (it.code + " " + (it.desc || "")).toLowerCase().includes(q);
    });
  }, [items, filter, domain]);

  const save = async () => {
    if (!editing) return;
    const { code, lo, hi, desc } = editing;
    if (Number(lo) > Number(hi)) {
      setErr("lo must be ≤ hi");
      return;
    }
    setErr("");
    try {
      await api.put(`/projects/${projectId}/rate-card/${code}`, {
        lo: Number(lo), hi: Number(hi), desc: desc || undefined,
      });
      setEditing(null);
      await load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
  };

  const reset = async (code) => {
    setErr("");
    try {
      await api.delete(`/projects/${projectId}/rate-card/${code}`);
      await load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
  };

  const overriddenCount = items.filter((i) => i.overridden).length;
  const cats = ["All", "Process Plant", "Infrastructure", "Real Estate"];

  return (
    <div data-testid="rate-card-editor" style={{
      border: "1px solid var(--pf-border)", borderRadius: 12, padding: 18,
      background: "var(--pf-surface)", display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <DollarSign size={22} style={{ color: "#F26B1F" }} />
        <div style={{ flex: 1 }}>
          <div className="pf-h2" style={{ fontSize: 17 }}>Project Rate-Card</div>
          <div className="pf-muted" style={{ fontSize: 12 }}>
            Override default INR/USD lo/hi rate bands for this project.
            Overrides apply automatically to BOQ runs and tender exports.
            {overriddenCount > 0 && <> · <strong style={{ color: "#F26B1F" }}>
              {overriddenCount} override{overriddenCount === 1 ? "" : "s"}</strong> active</>}
          </div>
        </div>
      </div>

      {err && <div data-testid="rate-card-err" style={{
        padding: 8, borderRadius: 6, fontSize: 12,
        background: "rgba(198,40,40,0.1)", color: "#FF8888",
        border: "1px solid rgba(198,40,40,0.3)",
      }}>{err}</div>}

      {/* Filter row */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
          <Search size={12} style={{ position: "absolute", top: 9, left: 8, color: "#888" }} />
          <input
            data-testid="rate-card-search"
            placeholder="Search code or description…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              width: "100%", padding: "6px 10px 6px 26px",
              fontSize: 12, borderRadius: 6,
              border: "1px solid var(--pf-border)",
              background: "var(--pf-surface-2)", color: "var(--pf-fg)",
            }}
          />
        </div>
        {cats.map((c) => (
          <button
            key={c}
            data-testid={`rate-card-filter-${c.replace(/\s/g, "")}`}
            onClick={() => setDomain(c)}
            className={domain === c ? "pf-btn pf-btn-primary" : "pf-btn"}
            style={{
              padding: "5px 10px", fontSize: 11, borderRadius: 4,
              cursor: "pointer", border: "1px solid var(--pf-border)",
              background: domain === c ? "var(--pf-ember)" : "var(--pf-surface-2)",
              color: domain === c ? "#fff" : "var(--pf-fg)",
            }}
          >{c}</button>
        ))}
      </div>

      <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
        Showing {visible.length} of {items.length} codes{busy ? " · loading…" : ""}
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
        <table data-testid="rate-card-table" style={{
          width: "100%", borderCollapse: "collapse", fontSize: 12,
        }}>
          <thead style={{ position: "sticky", top: 0, background: "var(--pf-surface-2)" }}>
            <tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
              <th style={{ padding: 6, textAlign: "left" }}>Code</th>
              <th style={{ padding: 6, textAlign: "left" }}>Description</th>
              <th style={{ padding: 6, textAlign: "left" }}>Unit</th>
              <th style={{ padding: 6, textAlign: "right" }}>Lo</th>
              <th style={{ padding: 6, textAlign: "right" }}>Hi</th>
              <th style={{ padding: 6, textAlign: "right" }}>Mid</th>
              <th style={{ padding: 6 }}>{ }</th>
              <th style={{ padding: 6 }}>{ }</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((it) => {
              const isEditing = editing && editing.code === it.code;
              return (
                <tr key={it.code}
                  data-testid={`rate-card-row-${it.code}`}
                  style={{
                    borderBottom: "1px solid var(--pf-border)",
                    background: isEditing ? "rgba(242,107,31,0.08)"
                                  : (it.overridden ? "rgba(242,107,31,0.04)" : "transparent"),
                  }}>
                  <td style={{ padding: 6, fontFamily: "monospace", fontSize: 11 }}>
                    {it.code}
                    {it.overridden && <span style={{
                      marginLeft: 4, padding: "1px 5px", fontSize: 9,
                      background: "var(--pf-ember)", color: "#fff",
                      borderRadius: 3, fontWeight: 700,
                    }}>OVR</span>}
                  </td>
                  <td style={{ padding: 6 }}>
                    {isEditing ? (
                      <input
                        data-testid={`rate-card-desc-${it.code}`}
                        value={editing.desc ?? ""}
                        onChange={(e) => setEditing({ ...editing, desc: e.target.value })}
                        style={{
                          width: "100%", fontSize: 11, padding: "2px 6px",
                          background: "var(--pf-surface)", color: "var(--pf-fg)",
                          border: "1px solid var(--pf-border)", borderRadius: 3,
                        }}
                      />
                    ) : it.desc}
                  </td>
                  <td style={{ padding: 6, fontSize: 11, color: "#888" }}>{it.unit}</td>
                  <td style={{ padding: 6, textAlign: "right" }}>
                    {isEditing ? (
                      <input
                        data-testid={`rate-card-lo-${it.code}`}
                        type="number" step="any"
                        value={editing.lo}
                        onChange={(e) => setEditing({ ...editing, lo: e.target.value })}
                        style={{
                          width: 88, fontSize: 11, padding: "2px 6px", textAlign: "right",
                          background: "var(--pf-surface)", color: "var(--pf-fg)",
                          border: "1px solid var(--pf-border)", borderRadius: 3,
                        }}
                      />
                    ) : `${it.ccy === "USD" ? "$" : "₹"}${Number(it.lo).toLocaleString()}`}
                  </td>
                  <td style={{ padding: 6, textAlign: "right" }}>
                    {isEditing ? (
                      <input
                        data-testid={`rate-card-hi-${it.code}`}
                        type="number" step="any"
                        value={editing.hi}
                        onChange={(e) => setEditing({ ...editing, hi: e.target.value })}
                        style={{
                          width: 88, fontSize: 11, padding: "2px 6px", textAlign: "right",
                          background: "var(--pf-surface)", color: "var(--pf-fg)",
                          border: "1px solid var(--pf-border)", borderRadius: 3,
                        }}
                      />
                    ) : `${it.ccy === "USD" ? "$" : "₹"}${Number(it.hi).toLocaleString()}`}
                  </td>
                  <td style={{ padding: 6, textAlign: "right", fontWeight: 600 }}>
                    {it.ccy === "USD" ? "$" : "₹"}
                    {Number(it.rate_mid).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </td>
                  <td style={{ padding: 6, textAlign: "center", whiteSpace: "nowrap" }}>
                    {isEditing ? (
                      <>
                        <button data-testid={`rate-card-save-${it.code}`}
                          onClick={save}
                          title="Save"
                          style={{ padding: 4, marginRight: 2,
                            background: "var(--pf-ember)", color: "#fff",
                            border: 0, borderRadius: 3, cursor: "pointer" }}>
                          <Check size={12} />
                        </button>
                        <button data-testid={`rate-card-cancel-${it.code}`}
                          onClick={() => setEditing(null)}
                          title="Cancel"
                          style={{ padding: 4, background: "transparent",
                            border: "1px solid var(--pf-border)",
                            color: "var(--pf-fg)", borderRadius: 3, cursor: "pointer" }}>
                          <X size={12} />
                        </button>
                      </>
                    ) : (
                      <button
                        data-testid={`rate-card-edit-${it.code}`}
                        onClick={() => setEditing({ code: it.code, lo: it.lo,
                                                       hi: it.hi, desc: it.desc })}
                        title="Edit override"
                        style={{ padding: 4, background: "transparent",
                          border: "1px solid var(--pf-border)",
                          color: "var(--pf-fg)", borderRadius: 3, cursor: "pointer" }}>
                        <Edit3 size={12} />
                      </button>
                    )}
                  </td>
                  <td style={{ padding: 6, textAlign: "center" }}>
                    {it.overridden && !isEditing && (
                      <button
                        data-testid={`rate-card-reset-${it.code}`}
                        onClick={() => reset(it.code)}
                        title="Revert to default"
                        style={{ padding: 4, background: "transparent",
                          border: "1px solid var(--pf-border)",
                          color: "var(--pf-fg)", borderRadius: 3, cursor: "pointer" }}>
                        <RotateCcw size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && !busy && (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 24, color: "#888" }}>
                No rate codes match this filter.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
